import { useEffect, useState } from 'react';
import { toast } from 'sonner';
import api from '@/lib/api';
import { getApiErrorMessage } from '@/stores/partnerAuthStore';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import type { AmendableInvoice } from '@/types/groups';
import { describeDiscount, type PromoCode } from '@/types/promoCodes';

interface ApplyPromoCodeDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  groupUuid: string;
  groupName: string;
  amendableInvoices: AmendableInvoice[];
  /** The page reloads the group, so nothing from the response is handed back. */
  onApplied: () => void;
}

export function ApplyPromoCodeDialog({ open, onOpenChange, groupUuid, groupName, amendableInvoices, onApplied }: ApplyPromoCodeDialogProps) {
  const [codes, setCodes] = useState<PromoCode[]>([]);
  const [selected, setSelected] = useState('');
  const [refusal, setRefusal] = useState('');
  const [isApplying, setIsApplying] = useState(false);

  useEffect(() => {
    let active = true;

    void (async () => {
      try {
        const response = await api.get('/promo-codes');
        // Only codes that can be applied right now. The API is still the
        // judge: a code can expire between this list and the click. Fixed
        // codes are left out: the type has no agreed currency, so the portal
        // neither creates nor hands one out.
        if (active) {
          setCodes((response.data.data as PromoCode[]).filter((code) => code.status === 'active' && code.discountType !== 'fixed'));
        }
      } catch (error) {
        if (active) toast.error(getApiErrorMessage(error), { id: 'apply-promo-codes-error' });
      }
    })();

    return () => {
      active = false;
    };
  }, []);

  const code = codes.find((candidate) => candidate.code === selected);
  const invoiceList = amendableInvoices.map((invoice) => invoice.invoiceNumber).join(', ');

  const apply = async () => {
    setRefusal('');
    setIsApplying(true);

    try {
      const response = await api.post(`/groups/${groupUuid}/promo-code`, { code: selected });
      const { amendedInvoices, notificationFailed } = response.data.data;
      const numbers = (amendedInvoices as { invoiceNumber: string }[]).map((invoice) => invoice.invoiceNumber).join(', ');

      if (numbers === '') {
        toast.success('Promo code applied.');
      } else if (notificationFailed) {
        toast.warning(`Promo code applied and invoice ${numbers} was reduced, but emailing it failed. Send it from the Invoices page.`);
      } else {
        // No promise of an email: an invoice reduced to nothing is settled
        // instead, and a draft that was never sent has no earlier copy.
        toast.success(`Promo code applied. Invoice ${numbers} was reduced.`);
      }

      onApplied();
      onOpenChange(false);
    } catch (error) {
      // A refusal is something staff can act on, so it stays in front of them.
      setRefusal(getApiErrorMessage(error));
      // Not in a finally: on success the dialog has already unmounted.
      setIsApplying(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Apply promo code</DialogTitle>
          <DialogDescription>
            Applied to {groupName}'s current subscription. A subscription takes one code, and it cannot be removed afterwards.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-2">
          <Label htmlFor="apply-promo-code">Promo code</Label>
          <Select value={selected} onValueChange={(value) => { setSelected(value); setRefusal(''); }}>
            <SelectTrigger id="apply-promo-code"><SelectValue placeholder="Choose a code" /></SelectTrigger>
            <SelectContent>
              {codes.map((candidate) => (
                <SelectItem key={candidate.id} value={candidate.code}>
                  {candidate.code} ({describeDiscount(candidate)})
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        {code && (
          <div className="space-y-1 rounded-md border p-3 text-sm">
            {code.discountType === 'free_period' ? (
              <>
                <p>{describeDiscount(code)} added to the current period.</p>
                <p className="text-muted-foreground">Invoices already issued are not changed.</p>
              </>
            ) : (
              <>
                <p>{describeDiscount(code)} from now on.</p>
                <p className="text-muted-foreground">
                  {amendableInvoices.length > 0
                    ? `Invoice ${invoiceList} was issued recently, so it will be reduced by ${code.discountValue}%.`
                    : 'No recent invoice can be reduced, so the discount starts with the next one.'}
                </p>
              </>
            )}
          </div>
        )}

        {refusal && <p className="text-sm text-destructive">{refusal}</p>}

        <DialogFooter>
          <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button type="button" disabled={!code || isApplying} onClick={apply}>Apply code</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
